import cx from "classnames";

import { Space, Trash } from "components/icons/generated";
import MetaInfoListItem from "components/MetaInfoList/Item";
import VendorConfigIcon from "components/VendorIcon";
import BaseAction from "ds/components/BaseAction";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import TextEllipsis from "ds/components/TextEllipsis";
import { SpaceAccessLevel, StackDependencyDetail } from "types/generated";
import { AccessResult } from "utils/access";

import DependencyItemName from "./Name";
import { OutputReferences } from "./OutputReferences";
import styles from "./styles.module.css";

type DependencyItemProps = {
  item: StackDependencyDetail;
  onDelete?: () => void;
  canManage: AccessResult;
  className?: string;
  onOutputReferencesEdit?: () => void;
  referenceCount?: number;
  graphMode?: boolean;
  isFocused?: boolean;
  isOutputReferencesDisallowed?: boolean;
  disallowedVendorName?: string;
};

const DependencyItem = ({
  item,
  onDelete,
  className,
  onOutputReferencesEdit,
  referenceCount,
  graphMode,
  isFocused,
  canManage,
  isOutputReferencesDisallowed,
  disallowedVendorName,
}: DependencyItemProps) => {
  return (
    <Box direction="column" className={cx(styles.item, { [styles.focused]: isFocused }, className)}>
      <Box direction="row" align="center" justify="between" gap="medium" padding="large" fullWidth>
        <Box
          className={styles.stackInfoWrapper}
          direction="column"
          align="start"
          justify="center"
          shrink="1"
          gap="medium"
          fullWidth
        >
          <Box direction="row" align="center" justify="start" fullWidth>
            <VendorConfigIcon vendorConfig={item.vendorConfig} className={styles.vendorIcon} />

            <TextEllipsis tooltip={item.name} tooltipWidthMode="maxWidthSm">
              {(props) =>
                item.space.accessLevel === SpaceAccessLevel.None ? (
                  <DependencyItemName
                    {...props}
                    name={item.name}
                    className={cx(styles.itemName, props.className)}
                  />
                ) : (
                  <BaseAction
                    {...props}
                    to={`/stack/${item.id}/dependencies`}
                    className={cx(styles.itemNameLink, props.className)}
                  >
                    <DependencyItemName name={item.name} className={styles.itemName} />
                  </BaseAction>
                )
              }
            </TextEllipsis>
          </Box>

          {!graphMode && (
            <OutputReferences
              onEdit={onOutputReferencesEdit}
              referenceCount={referenceCount}
              disallowed={isOutputReferencesDisallowed}
              vendorName={disallowedVendorName}
              canManage={canManage}
            />
          )}
        </Box>

        {onDelete && (canManage.result || canManage.message) && (
          <ButtonIcon
            disabled={!canManage.result}
            icon={Trash}
            onPress={onDelete}
            variant="ghostDanger"
          >
            {canManage.message ? `Delete dependency. ${canManage.message}` : "Delete dependency"}
          </ButtonIcon>
        )}
      </Box>

      <Box direction="row" className={styles.secondRow} align="center" justify="between">
        <MetaInfoListItem
          icon={Space}
          linkText={item.space.name}
          href={`/spaces/${item.space.id}`}
        />
      </Box>
    </Box>
  );
};

export default DependencyItem;
